import React from "react";

interface SlideContent {
  question?: string;
  subQuestion?: string;
  hints?: string[];
  mainEmoji?: string;
  sideEmojis?: string[];
  bgColor?: string;
  showSideDecorations?: boolean;
  showHints?: boolean;
  onEdit?: (content: SlideContent) => void;
}

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const QuestionPromptSlide2 = {
  id: "question-prompt-slide2",
  name: "Question / Prompt Slide Style 2",
  fields: [
    {
      id: "question",
      label: "Main Question",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subQuestion",
      label: "Sub Question",
      type: "text",
      defaultValue: "",
    },
    {
      id: "hints",
      label: "Hints (Array)",
      type: "text",
      defaultValue: JSON.stringify(["", "", ""]),
    },
    {
      id: "mainEmoji",
      label: "Main Emoji",
      type: "text",
      defaultValue: "🔍",
    },
    {
      id: "sideEmojis",
      label: "Side Emojis (Array)",
      type: "text",
      defaultValue: JSON.stringify(["💡", "✨"]),
    },
    {
      id: "showSideDecorations",
      label: "Show Side Decorations",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "showHints",
      label: "Show Hints",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #312e81 0%, #4338ca 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      question = "",
      subQuestion = "",
      hints = ["", "", ""],
      mainEmoji = "🔍",
      sideEmojis = ["💡", "✨"],
      showSideDecorations = true,
      showHints = true,
      bgColor = "linear-gradient(135deg, #312e81 0%, #4338ca 100%)",
      onEdit = () => {},
    } = content;

    // Parse arrays from string if needed
    const parsedHints = Array.isArray(hints)
      ? hints
      : typeof hints === "string"
        ? JSON.parse(hints)
        : ["", "", ""];

    const parsedSideEmojis = Array.isArray(sideEmojis)
      ? sideEmojis
      : typeof sideEmojis === "string"
        ? JSON.parse(sideEmojis)
        : ["💡", "✨"];

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleHintEdit = (index: number) => (value: string) => {
      const newHints = [...parsedHints];
      newHints[index] = value;
      handleEdit("hints")(JSON.stringify(newHints));
    };

    const handleSideEmojiEdit = (index: number) => (value: string) => {
      const newEmojis = [...parsedSideEmojis];
      newEmojis[index] = value;
      handleEdit("sideEmojis")(JSON.stringify(newEmojis));
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Side Decorations */}
        {showSideDecorations && (
          <>
            <div className="absolute left-0 inset-y-0 w-32 bg-gradient-to-r from-indigo-500/10 to-transparent" />
            <div className="absolute right-0 inset-y-0 w-32 bg-gradient-to-l from-indigo-500/10 to-transparent" />
          </>
        )}

        {/* Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-12">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center items-center gap-6 mb-6">
              {parsedSideEmojis.map((emoji, idx) => (
                <EditableDiv
                  key={idx}
                  text={emoji}
                  onEdit={handleSideEmojiEdit(idx)}
                  className="text-4xl transform hover:scale-110 transition-transform"
                />
              ))}
            </div>
            <EditableDiv
              text={mainEmoji}
              onEdit={handleEdit("mainEmoji")}
              className="text-6xl mb-6 inline-block transform hover:scale-110 transition-transform"
            />
          </div>

          {/* Question Section */}
          <div className="max-w-3xl w-full text-center mb-12">
            <EditableDiv
              text={question}
              onEdit={handleEdit("question")}
              className="text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 to-indigo-100"
            />
            <EditableDiv
              text={subQuestion}
              onEdit={handleEdit("subQuestion")}
              className="text-xl text-indigo-200/80"
            />
          </div>

          {/* Hints */}
          {showHints && (
            <div className="grid grid-cols-3 gap-6 max-w-4xl w-full">
              {parsedHints.map((hint, idx) => (
                <div
                  key={idx}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 transform hover:scale-105 transition-transform"
                >
                  <EditableDiv
                    text={hint}
                    onEdit={handleHintEdit(idx)}
                    className="text-lg text-indigo-100/90 text-center"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  },
};

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default QuestionPromptSlide2;
